import { useContext, useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { useHistory, useLocation } from "react-router-dom";

import AddIcon from "@mui/icons-material/Add";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import ViewAgendaOutlinedIcon from "@mui/icons-material/ViewAgendaOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import Card from "@mui/material/Card";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import OutlinedInput from "@mui/material/OutlinedInput";
import Select from "@mui/material/Select";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";

import { DatabaseContext, UserContext } from "../../context";
import firebase from "../../firebase";
import { makeid } from "../../utility";
import NoPermissionsMessage from "../NoPermissionsMessage";
import ToolbarWithLogo from "../ToolbarWithLogo";
import CalendarListView from "./CalendarListView";
import NewEventDialog from "./NewEventDialog";
import NodiantCalendar, { getCalendarEventColor } from "./NodiantCalendar";
import ViewEventDialog from "./ViewEventDialog";

function TabPanel({ children, value, index }) {
    return (
        <Box
            role="tabpanel"
            hidden={value !== index}
            id={`calendar-tabpanel-${index}`}
            aria-labelledby={`calendar-tab-${index}`}
        >
            {value === index && children}
        </Box>
    );
}

export default function CalendarPage() {
    const history = useHistory();
    const location = useLocation();
    const calendarSelectionTouched = useRef(false);

    const [newEventDialogOpen, setNewEventDialogOpen] = useState(false);
    const [viewEventDialogOpen, setViewEventDialogOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [selectedEvent, setSelectedEvent] = useState();
    const [userCanView, setUserCanView] = useState();
    const [userCanEdit, setUserCanEdit] = useState(false);
    const [availableEvents, setAvailableEvents] = useState([]);
    const [selectedCalendarKeys, setSelectedCalendarKeys] = useState([]);
    const [availableCalendars, setAvailableCalendars] = useState([]);
    const [currentTab, setCurrentTab] = useState(0);

    const { currentUserData } = useContext(UserContext);
    const {
        calendars,
        calendarsLoaded = false,
        events,
        eventsLoaded = false,
        folders,
        foldersLoaded = false,
    } = useContext(DatabaseContext);

    const calendarDataLoading =
        userCanView === undefined ||
        (userCanView &&
            (!calendarsLoaded || !eventsLoaded || !foldersLoaded));

    useEffect(() => {
        const unsubscribe = firebase.auth().onAuthStateChanged((user) => {
            if (!user) {
                history.replace(`/signin?url=${location.pathname}`);
            }
        });

        if (currentUserData && currentUserData.permissions) {
            const canView = currentUserData.permissions.viewCalendar;
            setUserCanView(canView);
            setUserCanEdit(Boolean(currentUserData.permissions.editCalendar));

            if (canView) {
                const calendarsArray = calendars.filter((calendar) => {
                    if (calendar.user === currentUserData.key) {
                        return true;
                    }

                    const hub = folders.find(
                        (folder) => folder.key === calendar.hub,
                    );

                    return Boolean(
                        hub &&
                        (hub.users.includes(currentUserData.key) ||
                            hub.managers.includes(currentUserData.key)),
                    );
                });
                const calendarKeys = new Set(
                    calendarsArray.map((calendar) => calendar.key),
                );

                setAvailableCalendars(calendarsArray);
                setAvailableEvents(
                    events.filter((event) => calendarKeys.has(event.calendar)),
                );
                setSelectedCalendarKeys((currentKeys) =>
                    calendarSelectionTouched.current
                        ? currentKeys.filter((key) => calendarKeys.has(key))
                        : Array.from(calendarKeys),
                );
            }
        }

        return () => unsubscribe();
    }, [
        calendars,
        currentUserData,
        events,
        folders,
        history,
        location.pathname,
    ]);

    const selectedCalendarKeySet = useMemo(
        () => new Set(selectedCalendarKeys),
        [selectedCalendarKeys],
    );

    const visibleEvents = useMemo(
        () =>
            availableEvents.filter((event) =>
                selectedCalendarKeySet.has(event.calendar),
            ),
        [availableEvents, selectedCalendarKeySet],
    );

    const eventsOnSelectedDay = useMemo(
        () =>
            visibleEvents
                .filter((event) =>
                    dayjs(event.date).isSame(dayjs(selectedDate), "day"),
                )
                .sort((a, b) => a.name.localeCompare(b.name)),
        [selectedDate, visibleEvents],
    );

    const calendarByKey = useMemo(
        () =>
            new Map(
                availableCalendars.map((calendar) => [calendar.key, calendar]),
            ),
        [availableCalendars],
    );

    const handleCalendarSelectionChange = (event) => {
        calendarSelectionTouched.current = true;
        setSelectedCalendarKeys(event.target.value);
    };

    const handleEventClick = (event) => () => {
        setSelectedEvent(event);
        setViewEventDialogOpen(true);
    };

    function addEvent(name, selectedCalendar, color) {
        const eventsRef = firebase.database().ref("events");
        const key = `-${makeid(20)}`;

        eventsRef.child(key).set({
            date: dayjs(selectedDate).toString(),
            name,
            calendar: selectedCalendar.key,
            color: color.replace("#", ""),
        });

        firebase
            .database()
            .ref(`calendars/${selectedCalendar.key}/events/`)
            .child(key)
            .set(true);

        setNewEventDialogOpen(false);
    }

    return (
        <>
            {userCanView && !calendarDataLoading && (
                <Box
                    sx={{
                        minHeight: {
                            xs: "calc(100dvh - 60px)",
                            sm: "calc(100vh - 128px)",
                        },
                        pb: 12,
                        backgroundColor: "background.default",
                    }}
                >
                    <Container
                        maxWidth="lg"
                        sx={{ px: { xs: 1.5, sm: 3 }, py: { xs: 2, sm: 3 } }}
                    >
                        <Card
                            variant="outlined"
                            sx={{
                                borderRadius: 3,
                                borderColor: "divider",
                                backgroundColor: "background.paper",
                                overflow: "hidden",
                                boxShadow: "0 12px 34px rgba(20, 35, 25, 0.06)",
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.5,
                                    px: { xs: 2, sm: 3 },
                                    py: 2.5,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 44,
                                        height: 44,
                                        borderRadius: 2,
                                        display: "grid",
                                        placeItems: "center",
                                        flexShrink: 0,
                                        color: "primary.main",
                                        backgroundColor:
                                            "rgba(0, 149, 61, 0.09)",
                                    }}
                                >
                                    <CalendarMonthOutlinedIcon />
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                        variant="h5"
                                        component="h1"
                                        sx={{ lineHeight: 1.1 }}
                                    >
                                        Calendar
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ mt: 0.5 }}
                                    >
                                        View events across your available
                                        calendars.
                                    </Typography>
                                </Box>
                                <Chip
                                    label={`${visibleEvents.length} ${
                                        visibleEvents.length === 1
                                            ? "event"
                                            : "events"
                                    }`}
                                    size="small"
                                    sx={{
                                        display: { xs: "none", sm: "flex" },
                                        backgroundColor:
                                            "rgba(0, 149, 61, 0.09)",
                                        color: "primary.dark",
                                    }}
                                />
                                {userCanEdit && (
                                    <Button
                                        variant="contained"
                                        disableElevation
                                        startIcon={<AddIcon />}
                                        onClick={() =>
                                            setNewEventDialogOpen(true)
                                        }
                                        disabled={
                                            availableCalendars.length === 0
                                        }
                                    >
                                        New event
                                    </Button>
                                )}
                            </Box>

                            <Box
                                sx={{
                                    px: { xs: 2, sm: 3 },
                                    pb: 2.5,
                                    borderBottom: "1px solid",
                                    borderColor: "divider",
                                }}
                            >
                                <FormControl fullWidth>
                                    <InputLabel id="calendar-filter-label">
                                        Visible calendars
                                    </InputLabel>
                                    <Select
                                        labelId="calendar-filter-label"
                                        multiple
                                        value={selectedCalendarKeys}
                                        onChange={handleCalendarSelectionChange}
                                        input={
                                            <OutlinedInput label="Visible calendars" />
                                        }
                                        renderValue={(selected) =>
                                            selected
                                                .map(
                                                    (key) =>
                                                        calendarByKey.get(key)
                                                            ?.name,
                                                )
                                                .filter(Boolean)
                                                .join(", ")
                                        }
                                        sx={{ borderRadius: 2 }}
                                    >
                                        {availableCalendars.map((calendar) => (
                                            <MenuItem
                                                key={calendar.key}
                                                value={calendar.key}
                                            >
                                                <Checkbox
                                                    checked={selectedCalendarKeySet.has(
                                                        calendar.key,
                                                    )}
                                                />
                                                <ListItemText
                                                    primary={calendar.name}
                                                />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>

                            <Tabs
                                value={currentTab}
                                onChange={(event, value) =>
                                    setCurrentTab(value)
                                }
                                aria-label="Calendar views"
                                sx={{
                                    px: { xs: 1, sm: 2 },
                                    minHeight: 52,
                                    "& .MuiTab-root": {
                                        minHeight: 52,
                                        fontWeight: 800,
                                        textTransform: "none",
                                    },
                                }}
                            >
                                <Tab
                                    id="calendar-tab-0"
                                    icon={<CalendarMonthOutlinedIcon />}
                                    iconPosition="start"
                                    label="Month"
                                />
                                <Tab
                                    id="calendar-tab-1"
                                    icon={<ViewAgendaOutlinedIcon />}
                                    iconPosition="start"
                                    label="List"
                                />
                            </Tabs>

                            <Box
                                sx={{
                                    borderTop: "1px solid",
                                    borderColor: "divider",
                                    backgroundColor: "background.default",
                                }}
                            >
                                <TabPanel value={currentTab} index={0}>
                                    <Box
                                        sx={{
                                            display: "grid",
                                            gridTemplateColumns: {
                                                xs: "minmax(0, 1fr)",
                                                md: "minmax(0, 1.2fr) minmax(320px, 0.8fr)",
                                            },
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                p: { xs: 2, sm: 3 },
                                                borderRight: {
                                                    md: "1px solid",
                                                },
                                                borderBottom: {
                                                    xs: "1px solid",
                                                    md: "none",
                                                },
                                                borderColor: "divider",
                                                backgroundColor:
                                                    "background.paper",
                                            }}
                                        >
                                            <Typography
                                                variant="subtitle2"
                                                sx={{
                                                    mb: 1.5,
                                                    fontWeight: 800,
                                                }}
                                            >
                                                Select a date
                                            </Typography>
                                            <NodiantCalendar
                                                selectedDate={selectedDate}
                                                onDateChange={setSelectedDate}
                                                events={visibleEvents}
                                                spacious
                                            />
                                        </Box>

                                        <Box
                                            sx={{
                                                p: { xs: 2, sm: 3 },
                                                minWidth: 0,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "flex-start",
                                                    gap: 1,
                                                    mb: 2,
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        flex: 1,
                                                        minWidth: 0,
                                                    }}
                                                >
                                                    <Typography
                                                        variant="h6"
                                                        component="h2"
                                                        sx={{ lineHeight: 1.1 }}
                                                    >
                                                        {dayjs(
                                                            selectedDate,
                                                        ).format(
                                                            "dddd, D MMMM",
                                                        )}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{ mt: 0.5 }}
                                                    >
                                                        {eventsOnSelectedDay.length ===
                                                        1
                                                            ? "1 event"
                                                            : `${eventsOnSelectedDay.length} events`}
                                                    </Typography>
                                                </Box>
                                                {userCanEdit && (
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        startIcon={<AddIcon />}
                                                        onClick={() =>
                                                            setNewEventDialogOpen(
                                                                true,
                                                            )
                                                        }
                                                        disabled={
                                                            availableCalendars.length ===
                                                            0
                                                        }
                                                    >
                                                        Add
                                                    </Button>
                                                )}
                                            </Box>

                                            {eventsOnSelectedDay.length ===
                                            0 ? (
                                                <Box
                                                    sx={{
                                                        minHeight: 330,
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        alignItems: "center",
                                                        justifyContent:
                                                            "center",
                                                        px: 3,
                                                        textAlign: "center",
                                                        color: "text.secondary",
                                                    }}
                                                >
                                                    <EventBusyOutlinedIcon
                                                        sx={{
                                                            mb: 1.25,
                                                            fontSize: 42,
                                                            opacity: 0.4,
                                                        }}
                                                    />
                                                    <Typography
                                                        variant="subtitle1"
                                                        sx={{
                                                            color: "text.primary",
                                                            fontWeight: 800,
                                                        }}
                                                    >
                                                        No events on this date
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        Choose another date
                                                        {userCanEdit
                                                            ? " or add a new event."
                                                            : "."}
                                                    </Typography>
                                                </Box>
                                            ) : (
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        gap: 1,
                                                        maxHeight: {
                                                            md: 500,
                                                        },
                                                        overflowY: "auto",
                                                        pr: { md: 0.5 },
                                                    }}
                                                >
                                                    {eventsOnSelectedDay.map(
                                                        (event) => (
                                                            <ButtonBase
                                                                key={event.key}
                                                                onClick={handleEventClick(
                                                                    event,
                                                                )}
                                                                sx={{
                                                                    display:
                                                                        "flex",
                                                                    alignItems:
                                                                        "center",
                                                                    gap: 1.25,
                                                                    width: "100%",
                                                                    p: 1.5,
                                                                    borderRadius: 2,
                                                                    border: "1px solid",
                                                                    borderColor:
                                                                        "divider",
                                                                    backgroundColor:
                                                                        "background.paper",
                                                                    textAlign:
                                                                        "left",
                                                                    justifyContent:
                                                                        "flex-start",
                                                                    "&:hover": {
                                                                        borderColor:
                                                                            "rgba(0, 149, 61, 0.35)",
                                                                        boxShadow:
                                                                            "0 3px 12px rgba(20, 35, 25, 0.07)",
                                                                    },
                                                                }}
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        width: 10,
                                                                        minHeight: 46,
                                                                        alignSelf:
                                                                            "stretch",
                                                                        borderRadius: 1,
                                                                        flexShrink: 0,
                                                                        backgroundColor:
                                                                            getCalendarEventColor(
                                                                                event.color,
                                                                            ),
                                                                    }}
                                                                />
                                                                <Box
                                                                    sx={{
                                                                        flex: 1,
                                                                        minWidth: 0,
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        variant="subtitle2"
                                                                        sx={{
                                                                            fontWeight: 800,
                                                                            overflow:
                                                                                "hidden",
                                                                            textOverflow:
                                                                                "ellipsis",
                                                                            whiteSpace:
                                                                                "nowrap",
                                                                        }}
                                                                    >
                                                                        {
                                                                            event.name
                                                                        }
                                                                    </Typography>
                                                                    <Typography
                                                                        variant="body2"
                                                                        color="text.secondary"
                                                                        sx={{
                                                                            overflow:
                                                                                "hidden",
                                                                            textOverflow:
                                                                                "ellipsis",
                                                                            whiteSpace:
                                                                                "nowrap",
                                                                        }}
                                                                    >
                                                                        {calendarByKey.get(
                                                                            event.calendar,
                                                                        )
                                                                            ?.name ||
                                                                            "Calendar"}
                                                                    </Typography>
                                                                </Box>
                                                            </ButtonBase>
                                                        ),
                                                    )}
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                </TabPanel>

                                <TabPanel value={currentTab} index={1}>
                                    <Box sx={{ p: { xs: 2, sm: 3 } }}>
                                        <CalendarListView
                                            handleEventClick={handleEventClick}
                                            events={visibleEvents}
                                            calendars={availableCalendars}
                                        />
                                    </Box>
                                </TabPanel>
                            </Box>
                        </Card>
                    </Container>

                    <NewEventDialog
                        open={newEventDialogOpen}
                        onClose={() => setNewEventDialogOpen(false)}
                        date={selectedDate}
                        addEvent={addEvent}
                        calendars={availableCalendars}
                    />
                    <ViewEventDialog
                        open={viewEventDialogOpen}
                        onClose={() => setViewEventDialogOpen(false)}
                        event={selectedEvent}
                    />
                </Box>
            )}

            {calendarDataLoading && (
                <Box
                    sx={{
                        minHeight: {
                            xs: "calc(100dvh - 60px)",
                            sm: "calc(100vh - 128px)",
                        },
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1.5,
                        color: "text.secondary",
                        backgroundColor: "background.default",
                    }}
                >
                    <CircularProgress size={28} />
                    <Typography variant="body2">
                        Loading your calendars…
                    </Typography>
                </Box>
            )}
            {userCanView === false && <NoPermissionsMessage />}
            <ToolbarWithLogo />
        </>
    );
}
